import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, apiRequest } from '@/lib/api/client';
import { advisorApi } from '@/features/workspace/api';
import { useAdvisorText as useStudyText } from '../labels';
import { useI18n } from '@/hooks/useI18n';
import type { AdvisorCandidate } from './AdvisorDoctoralProcessPage';
import '@/features/doctoral/dp120.css';
interface Meeting {
  id: string;
  student_id: string;
  student_name: string;
  advisor_name: string;
  title: string;
  scheduled_at: string;
  location: string;
  agenda: string;
  status: string;
}
interface Message {
  id: string;
  sender_name: string;
  body: string;
  created_at: string;
}
interface Conversation {
  id: string;
  messages: Message[];
  next_cursor?: string | null;
}
const base = '/doctoral/advisor-exchange';
export function AdvisorExchangePage({
  onClose,
  advisor = false,
  embedded = false,
  view = 'messages',
}: {
  onClose: () => void;
  advisor?: boolean;
  embedded?: boolean;
  view?: 'messages' | 'meetings';
}) {
  const st = useStudyText();
  const { locale } = useI18n();
  const [params, setParams] = useSearchParams();
  const student = params.get('student_id') || '';
  const [items, setItems] = useState<AdvisorCandidate[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [conversation, setConversation] = useState<Conversation>();
  const [contact, setContact] = useState<{ name: string; academiaId: string } | null>();
  const [body, setBody] = useState('');
  const [draft, setDraft] = useState({ title: '', scheduled_at: '', location: '', agenda: '' });
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [older, setOlder] = useState<Message[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null>();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (advisor)
      void advisorApi
        .module('doctoral_process')
        .then((p) => {
          if (!cancelled)
            setItems((p.items as AdvisorCandidate[]).filter((c) => c.student_user_id));
        })
        .catch((e) => {
          if (!cancelled) {
            setError(String(e.message));
            if (e instanceof ApiError && [403, 404].includes(e.status)) {
              setConversation(undefined);
              setOlder([]);
            }
          }
        });
    return () => {
      cancelled = true;
    };
  }, [advisor]);
  useEffect(() => {
    setConversation(undefined);
    setOlder([]);
    setOlderCursor(undefined);
  }, [advisor, student, view]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!document.hidden && !busy) setTick((t) => t + 1);
    }, 15000);
    return () => window.clearInterval(timer);
  }, [busy]);
  useEffect(() => {
    let cancelled = false;

    if (view === 'meetings') {
      void apiRequest<Meeting[]>(`${base}/meetings`, { channel: 'platform' })
        .then((p) => {
          if (!cancelled) setMeetings(p);
        })
        .catch((e) => {
          if (!cancelled) {
            setError(String(e.message));
            if (e instanceof ApiError && [403, 404].includes(e.status)) {
              setConversation(undefined);
              setOlder([]);
            }
          }
        });
    } else if (!advisor || student) {
      void apiRequest<Conversation>(`${base}/messages`, {
        channel: 'platform',
        query: { student_id: student || undefined },
      })
        .then((p) => {
          if (!cancelled) {
            setConversation(p);
            setOlder((old) => [...new Map([...old, ...p.messages].map((m) => [m.id, m])).values()]);
            setError('');
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setError(String(e.message));
            if (e instanceof ApiError && [403, 404].includes(e.status)) {
              setConversation(undefined);
              setOlder([]);
            }
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [advisor, student, view, tick]);
  useEffect(() => {
    if (advisor) return;
    let cancelled = false;
    void apiRequest<{ advisor: { name: string; academiaId: string } | null }>(
      '/doctoral/services/advisor',
      { channel: 'platform' },
    )
      .then((data) => {
        if (!cancelled) setContact(data.advisor);
      })
      .catch(() => {
        if (!cancelled) setContact(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [advisor, tick]);
  const loadOlder = async () => {
    if (!conversation) return;
    setBusy(true);
    try {
      const page = await apiRequest<Conversation>(`${base}/messages`, {
        channel: 'platform',
        query: {
          student_id: student || undefined,
          before: (olderCursor === undefined ? conversation.next_cursor : olderCursor) || undefined,
        },
      });
      setOlder((previous) => [...page.messages, ...previous]);
      setOlderCursor(page.next_cursor || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : st('Ошибка'));
    } finally {
      setBusy(false);
    }
  };
  const act = async (path: string, data: unknown, method: 'POST' | 'PATCH' = 'POST') => {
    setBusy(true);
    setError('');
    try {
      await apiRequest(path, { channel: 'platform', method, body: data });
      setTick((t) => t + 1);
      setRequestId(crypto.randomUUID());
      setBody('');
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : st('Ошибка'));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const time = (value: string) =>
    new Date(value).toLocaleString(locale === 'kz' ? 'kk-KZ' : locale === 'en' ? 'en-GB' : 'ru-RU');
  return (
    <div
      className="dp120-root open"
      style={embedded ? { position: 'relative', inset: 'auto', zIndex: 'auto' } : undefined}
    >
      <div className="dp120-shell" style={embedded ? { minHeight: 'auto' } : undefined}>
        <main className="dp120-main">
          <button className="dp120-back" onClick={onClose}>
            ← {st('Назад')}
          </button>
          <h2>{st(view === 'messages' ? 'Связь с эдвайзером' : 'Встречи и консультации')}</h2>
          {!advisor && contact && (
            <p>
              {contact.name} · {contact.academiaId}
            </p>
          )}
          {!advisor && contact === null && (
            <p>{st('Эдвайзер пока не назначен. Обратитесь к администратору.')}</p>
          )}
          {advisor && (
            <label className="dp120-field">
              <span>{st('Докторант')}</span>
              <select
                value={student}
                disabled={busy}
                onChange={(e) => {
                  setParams({ student_id: e.target.value });
                  setRequestId(crypto.randomUUID());
                  setBody('');
                }}
              >
                <option value="">{st('Выберите…')}</option>
                {items.map((c) => (
                  <option key={c.id} value={c.student_user_id!}>
                    {c.full_name || c.academia_id}
                  </option>
                ))}
              </select>
            </label>
          )}
          {error && <p role="alert">{error}</p>}
          {view === 'messages' && conversation && (
            <>
              <div aria-live="polite">
                {(olderCursor === undefined ? conversation.next_cursor : olderCursor) && (
                  <button disabled={busy} onClick={loadOlder}>
                    {st('Предыдущие сообщения')}
                  </button>
                )}
                {[...new Map([...older, ...conversation.messages].map((m) => [m.id, m])).values()]
                  .sort(
                    (a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id),
                  )
                  .map((m) => (
                    <article className="dp120-card" key={m.id}>
                      <div className="dp120-card-body">
                        <strong>{m.sender_name}</strong>
                        <small> · {time(m.created_at)}</small>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{m.body}</p>
                      </div>
                    </article>
                  ))}
              </div>
              <label className="dp120-field">
                <span>{st('Сообщение')}</span>
                <textarea
                  value={body}
                  disabled={busy}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setRequestId(crypto.randomUUID());
                  }}
                  maxLength={20000}
                />
              </label>
              <button
                className="dp120-btn gold"
                disabled={busy || !body.trim()}
                onClick={() =>
                  act(`${base}/messages`, {
                    student_id: student || undefined,
                    body,
                    request_id: requestId,
                  })
                }
              >
                {st('Отправить')}
              </button>
              <button className="dp120-mini" disabled={busy} onClick={() => setTick((t) => t + 1)}>
                {st('Обновить')}
              </button>
            </>
          )}
          {view === 'meetings' && (
            <>
              {advisor && (
                <section className="dp120-card">
                  <div className="dp120-card-body">
                    <h3>{st('Назначить встречу')}</h3>
                    {(['title', 'scheduled_at', 'location', 'agenda'] as const).map((key, i) => (
                      <label key={key} className="dp120-field">
                        <span>
                          {st(['Название', 'Дата и время', 'Место или ссылка', 'Повестка'][i])}
                        </span>
                        <input
                          type={key === 'scheduled_at' ? 'datetime-local' : 'text'}
                          value={draft[key]}
                          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                        />
                      </label>
                    ))}
                    <button
                      className="dp120-btn gold"
                      disabled={busy || !student || !draft.title.trim() || !draft.scheduled_at}
                      onClick={async () => {
                        if (
                          await act(`${base}/meetings`, {
                            ...draft,
                            student_id: student,
                            request_id: requestId,
                            scheduled_at: new Date(draft.scheduled_at).toISOString(),
                          })
                        )
                          setDraft({ title: '', scheduled_at: '', location: '', agenda: '' });
                      }}
                    >
                      {st('Назначить')}
                    </button>
                  </div>
                </section>
              )}
              {meetings
                .filter((m) => !student || m.student_id === student)
                .map((m) => (
                  <article className="dp120-card" key={m.id}>
                    <div className="dp120-card-body">
                      <h3>{m.title}</h3>
                      <p>
                        {m.student_name} · {m.advisor_name} · {time(m.scheduled_at)}
                      </p>
                      <p>{m.location}</p>
                      <p>{m.agenda}</p>
                      <b>{st(m.status)}</b>
                      <div className="dp120-row-actions">
                        {(advisor
                          ? m.status === 'PENDING'
                            ? ['CANCELLED']
                            : m.status === 'ACCEPTED'
                              ? ['COMPLETED', 'CANCELLED']
                              : []
                          : m.status === 'PENDING'
                            ? ['ACCEPTED', 'DECLINED']
                            : []
                        ).map((status) => (
                          <button
                            key={status}
                            className="dp120-mini"
                            disabled={busy}
                            onClick={() => act(`${base}/meetings/${m.id}`, { status }, 'PATCH')}
                          >
                            {st(status)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              {!meetings.length && <p>{st('Встреч пока нет')}</p>}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
